
import { Component, OnInit, Input, Renderer2 } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CpMarketplaceApiService } from '../../services/cp-marketplace-api.service';
import { SharedDataService } from '../../services/shared-data.service';
import { DomSanitizer } from '@angular/platform-browser';
import interact from 'interactjs';
import jsPDF from 'jspdf';
import { COMMON_MESSAGES } from 'src/app/messages';

@Component({
  selector: 'app-upload-image-modal',
  templateUrl: './upload-image-modal.component.html',
  styleUrls: ['./upload-image-modal.component.scss']
})

/**
 * The name of the component says Upload Image but the component is actually about Uploading Letterhead.
 * This component takes care of uploading letterhead image and then drawing a draggable area on the image.
 * The draggable area is used to define the print area on the letterhead.
 * 
 * The computed print area is then saved to the backend.
 * 
 * This component heavily relies on interact.js for the draggable area.
 */
export class UploadImageModalComponent implements OnInit {
  image_file: string;
  uploadedPdfKey = '';
  fileTypes = ['.jpg', '.jpeg', '.JPG', '.JPEG'];

  constructor(
    public activeModal: NgbActiveModal,
    private apiService: CpMarketplaceApiService,
    private toastr: ToastrService,
    private sharedData: SharedDataService,
    public sanitizer: DomSanitizer,
    private renderer2: Renderer2
  ) {
  }

  ngOnInit() {
  }

  onSubmit() {
    if (!this.image_file) {
      this.toastr.warning(COMMON_MESSAGES.LETTERHEAD_UPLOADED_INVALID);
      return;
    }

    let containerCoords: any = document.getElementById('letterhead').getBoundingClientRect();
    let usableAreaCoords: any = document.getElementById('resize-container').getBoundingClientRect();

    let left = usableAreaCoords.x - containerCoords.x;
    let right = containerCoords.width - usableAreaCoords.width - left;
    let top = usableAreaCoords.y - containerCoords.y;
    let bottom = containerCoords.height - usableAreaCoords.height - top;

    left = (left / containerCoords.width);
    right = (right / containerCoords.width);
    top = (top / containerCoords.height);
    bottom = (bottom / containerCoords.height);

    if (left && right && top && bottom) {
      this.saveLetterHeadAndDimensions(left, right, top, bottom);
    } else {
      this.toastr.error(COMMON_MESSAGES.LETTERHEAD_ADJUSTMENT_BOX_ERROR);
    }
  }

  saveLetterHeadAndDimensions(left, right, top, bottom) {
    const uploadRequest: any = {};
    const corporate_id = this.sharedData.getUserDataFromLS().corporateId;

    uploadRequest.cover_letter_head_image = this.uploadedPdfKey;
    uploadRequest.left_right = [left, right].join(',');
    uploadRequest.top_bottom = [top, bottom].join(',');

    this.apiService.uploadLetterhead(uploadRequest, corporate_id).subscribe((resp: any) => {
      if (resp.error) {
        this.toastr.warning(COMMON_MESSAGES.LETTERHEAD_UPLOAD_FAILED);
      } else {
        this.toastr.success(COMMON_MESSAGES.LETTERHEAD_UPLOAD_SUCCESS);
        this.activeModal.close();
      }
    });
  }

  onConvertToBase64(file) {
    const reader = new FileReader();
    return new Promise((resolve) => {
      reader.onload = (e) => resolve(reader.result);
      reader.readAsDataURL(file);
    });
  }

  onFileChange(file) {

    this.onConvertToBase64(file).then((fileDataURI: any) => {
      this.image_file = <string>fileDataURI;
      /**
       * We need to upload the image as a PDF file. Hence this fancy code below. 
       * Also need to upload PDF using file key which needs binary data - not a 64 Bit DataURI String
       * Step 1: read Image to PDF library
       * Step 2: get the image as a PDF in binary(blob) format
       * Step 3: create a multipart form with the binary PDF blob for API request
       */

      const pdfletter = new jsPDF();
      pdfletter.addImage(fileDataURI, 'JPG', 0, 0, 210, 297);
      const pdfDataBlob = pdfletter.output('blob');
      const formDataRequest = new FormData();
      formDataRequest.append('files', pdfDataBlob, 'letterhead.pdf');

      this.apiService.uploadFileAndGetToken(formDataRequest).subscribe((respKey) => {
        this.uploadedPdfKey = respKey[0];
        this.toastr.success('Letterhead Image uploaded. Please draw the print area using the draggable area.');
      });
    });

    window.setTimeout(() => {
      const draggableEl = this.renderer2.selectRootElement('.resize-container');
      this.initInteract(draggableEl);
    }, 500);
  }

  initInteract(draggableEl) {
    interact(draggableEl)
      .draggable({
        onmove: draggableEl.dragMoveListener,
        modifiers: [
          interact.modifiers.restrict({
            restriction: 'parent',
            elementRect: { top: 0, left: 0, bottom: 1, right: 1 }
          }),
        ],
      })
      .resizable({
        // resize from all edges and corners
        edges: { left: true, right: true, bottom: true, top: true },
        squareResize: true,
        modifiers: [
          interact.modifiers.restrict({
            restriction: 'parent',
            endOnly: true,
            elementRect: {
              top: 0,
              left: 0,
              bottom: 1,
              right: 1
            },
          }),
        ],
        inertia: true
      })
      .on('resizemove', function (event) {
        const eventTypecasted = <any>event;
        const target = event.target;
        let x = (parseFloat(target.getAttribute('data-x')) || 0),
          y = (parseFloat(target.getAttribute('data-y')) || 0);

        // update the element's style
        target.style.width = eventTypecasted.rect.width + 'px';
        target.style.height = eventTypecasted.rect.height + 'px';

        // translate when resizing from top or left edges
        x += eventTypecasted.deltaRect.left;
        y += eventTypecasted.deltaRect.top;

        target.style.webkitTransform = target.style.transform =
          'translate(' + x + 'px,' + y + 'px)';
        target.setAttribute('data-x', x);
        target.setAttribute('data-y', y);
        target.textContent = Math.round(eventTypecasted.rect.width) + '\u00D7' + Math.round(eventTypecasted.rect.height);
      });
  }

}




